import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Table,
  Space,
  Button,
  Input,
  Select,
  DatePicker,
  Tag,
  Modal,
  Collapse,
  Checkbox,
  Switch,
  Descriptions,
  Typography,
  Tooltip,
  Alert,
  Grid,
  List,
  Pagination,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { useSearchParams } from 'react-router-dom';
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import API_CONFIG from '../config/api';
import AuditWorkspace, { AuditDetailContent, AuditField, AuditToolbar, AuditReadError, AuditStatus, auditActionLabel, auditDateLabel } from './AuditWorkspace';
import { MODULE_LABELS } from '../mobile/audit/auditMobileApi';
import {
  createAuditRequestGate,
  isAuditRequestCanceled,
  shouldLoadAuditPageImmediately,
} from "./auditRequestGate";

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

const ACTIONS_WITH_ENRICHED_DETAILS = new Set(["CREATE", "UPDATE", "DELETE"]);

const DETAIL_KEY_LABELS = {
  created_at: "Date de creation",
  updated_at: "Date de mise a jour",
  updated_fields: "Champs mis a jour",
  changed_fields: "Champs modifies",
  modified_fields: "Champs modifies",
  old_values: "Anciennes valeurs",
  new_values: "Nouvelles valeurs",
  old_value: "Ancienne valeur",
  new_value: "Nouvelle valeur",
  old_total_amount: "Ancien montant total",
  total_amount: "Nouveau montant total",
  payment_number: "No paiement",
  invoice_number: "No facture",
  bill_number: "No facture fournisseur",
  shipment_number: "No dossier",
  item_code: "Code article",
  client_name: "Client",
  supplier_name: "Fournisseur",
  order_id: "Commande",
  product_id: "Produit",
  company_name: "Entreprise",
  warehouse_name: "Établissement",
  payment_type: "Type de paiement",
  resource_id: "ID ressource",
};

const IDENTIFIER_KEYS = [
  "payment_number",
  "invoice_number",
  "bill_number",
  "shipment_number",
  "item_code",
  "name",
  "company_name",
  "client_name",
  "supplier_name",
  "email",
  "order_id",
  "product_id",
];

const isPlainObject = (value) =>
  Object.prototype.toString.call(value) === "[object Object]";

const formatDetailKey = (key = "") => {
  if (DETAIL_KEY_LABELS[key]) return DETAIL_KEY_LABELS[key];

  const cleaned = key.includes(".") ? key.split(".").pop() : key;
  return cleaned
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const parseAuditDetails = (rawDetails) => {
  if (!rawDetails) return null;

  if (typeof rawDetails === "string") {
    try {
      return JSON.parse(rawDetails);
    } catch {
      return { raw_details: rawDetails };
    }
  }

  return rawDetails;
};

const extractFlatDetails = (details, maxItems = 40) => {
  if (!isPlainObject(details)) return [];

  const items = [];
  const visit = (obj, parentKey = "", depth = 0) => {
    Object.entries(obj).forEach(([key, value]) => {
      if (value === undefined || items.length >= maxItems) return;

      const currentKey = parentKey ? `${parentKey}.${key}` : key;

      if (isPlainObject(value) && depth < 1) {
        visit(value, currentKey, depth + 1);
        return;
      }

      items.push({ key: currentKey, value });
    });
  };

  visit(details);
  return items;
};

const extractChangedFields = (details) => {
  if (!isPlainObject(details)) return [];

  const fields = new Set();
  const registerField = (value) => {
    if (typeof value === "string" && value.trim()) {
      fields.add(value.trim());
    }
  };

  [details.updated_fields, details.changed_fields, details.modified_fields]
    .filter(Array.isArray)
    .forEach((arr) => arr.forEach(registerField));

  [details.old_values, details.oldValues, details.before, details.new_values, details.newValues, details.after]
    .filter(isPlainObject)
    .forEach((values) => Object.keys(values).forEach(registerField));

  Object.keys(details).forEach((key) => {
    if (key === 'old_values' || key === 'new_values') return;
    if (key.startsWith("old_")) fields.add(key.replace(/^old_/, ""));
    if (key.startsWith("new_")) fields.add(key.replace(/^new_/, ""));
    if (key.startsWith("previous_")) fields.add(key.replace(/^previous_/, ""));
  });

  return Array.from(fields);
};

const extractBeforeAfterPairs = (details) => {
  if (!isPlainObject(details)) return [];

  const pairs = [];
  const seen = new Set();

  const oldValues = details.old_values || details.oldValues || details.before;
  const newValues = details.new_values || details.newValues || details.after;

  if (isPlainObject(oldValues) || isPlainObject(newValues)) {
    const keys = new Set([
      ...Object.keys(isPlainObject(oldValues) ? oldValues : {}),
      ...Object.keys(isPlainObject(newValues) ? newValues : {}),
    ]);

    keys.forEach((field) => {
      pairs.push({
        field,
        before: oldValues ? oldValues[field] : undefined,
        after: newValues ? newValues[field] : undefined,
      });
      seen.add(field);
    });
  }

  Object.entries(details).forEach(([key, beforeValue]) => {
    if (key === 'old_values') return;
    if (!key.startsWith("old_") && !key.startsWith("previous_")) return;

    const field = key.replace(/^old_/, "").replace(/^previous_/, "");
    if (!field || seen.has(field)) return;

    const afterValue = details[`new_${field}`] ?? details[field];
    pairs.push({ field, before: beforeValue, after: afterValue });
    seen.add(field);
  });

  return pairs;
};

const buildActionSummary = (log, details) => {
  if (!log) return null;

  const resourceText = log.resource_type
    ? `${log.resource_type}${log.resource_id ? ` #${log.resource_id}` : ""}`
    : log.resource_id
      ? `ressource #${log.resource_id}`
      : "ressource";

  const identifier = IDENTIFIER_KEYS.find(
    (key) => details && details[key] !== undefined && details[key] !== null
  );
  const identifierValue = identifier ? details[identifier] : null;
  const isIdentifierPrimitive =
    ["string", "number", "boolean"].includes(typeof identifierValue);
  const identifierText =
    identifier && isIdentifierPrimitive ? ` (${identifierValue})` : "";

  if (log.action_type === "CREATE") {
    return `Création de ${resourceText}${identifierText}`;
  }
  if (log.action_type === "UPDATE") {
    return `Mise à jour de ${resourceText}${identifierText}`;
  }
  if (log.action_type === "DELETE") {
    return `Suppression de ${resourceText}${identifierText}`;
  }

  return null;
};

const AuditLogs = ({ mobile = false }) => {
  const { token, user } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const [searchParams, setSearchParams] = useSearchParams();
  const previousSelectionRef = useRef(`${selectedCompany}:${selectedWarehouse}`);
  useEffect(() => {
    const selection = `${selectedCompany}:${selectedWarehouse}`;
    if (previousSelectionRef.current !== selection) {
      previousSelectionRef.current = selection;
      setSearchParams((params) => { params.delete('warehouseId'); params.delete('logId'); return params; }, { replace: true });
    }
  }, [selectedCompany, selectedWarehouse, setSearchParams]);
  const screens = useBreakpoint();
  const isMobile = mobile || !screens.md;
  const isSuperAdmin =
    user?.is_superadmin === true || Number(user?.is_superadmin) === 1;

  const [loading, setLoading] = useState(false);
  const [readError, setReadError] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [auditScope, setAuditScope] = useState(() => searchParams.get('scope') === 'all' ? 'all' : 'company');
  const effectiveAuditScope =
    isSuperAdmin && auditScope === "all" ? "all" : "company";
  const warehouseId = effectiveAuditScope === 'all' ? null : searchParams.get('warehouseId') || selectedWarehouse;
  const auditScopeKey =
    effectiveAuditScope === "all"
      ? `user:${user?.id}:all`
      : `user:${user?.id}:company:${selectedCompany || "none"}:warehouse:${warehouseId || 'all'}`;
  const latestAuditScopeKeyRef = useRef(auditScopeKey);
  useLayoutEffect(() => {
    latestAuditScopeKeyRef.current = auditScopeKey;
  }, [auditScopeKey]);
  const previousAuditScopeKeyRef = useRef(auditScopeKey);
  const requestGateRef = useRef(null);
  if (!requestGateRef.current) {
    requestGateRef.current = createAuditRequestGate();
  }

  const [filters, setFilters] = useState(() => ({
    userSearch: "",
    module: searchParams.get('module') || null,
    actionType: null,
    status: searchParams.get('status') || null,
    dateRange: searchParams.get('startDate') && searchParams.get('endDate')
      ? [dayjs(searchParams.get('startDate')), dayjs(searchParams.get('endDate'))] : null,
    ipAddress: null,
    includeAuth: searchParams.get('includeAuth') !== 'false',
    criticalOnly: searchParams.get('criticalOnly') === 'true',
  }));

  const moduleLabels = useMemo(() => MODULE_LABELS, []);

  const availableModules = useMemo(
    () =>
      Object.keys(moduleLabels).map((value) => ({
        value,
        label: moduleLabels[value],
      })),
    [moduleLabels]
  );

  const actionTypes = ["CREATE", "UPDATE", "DELETE", "READ", "VIEW_HISTORY", "ARCHIVE", "EXPORT"];

  const getModuleLabel = (moduleKey) => {
    if (!moduleKey) return "N/A";
    return moduleLabels[moduleKey] || moduleKey;
  };

  const renderStatusTag = (status) => {
    return <AuditStatus status={status} />;
  };

  const renderMethodTag = (method) => {
    if (!method) return <Text type="secondary">-</Text>;
    return <Text code>{method}</Text>;
  };

  const getUserDisplay = (record) => {
    if (record.username) {
      return <Text strong>{record.username}</Text>;
    }
    if (record.user_id) {
      return <Text type="secondary">Utilisateur #{record.user_id}</Text>;
    }
    return <Tag color="default">Système</Tag>;
  };

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const filtersRef = useRef(appliedFilters);
  filtersRef.current = appliedFilters;
  const publishedLogsQueryRef = useRef(null);
  const loadLogs = useCallback(async (
    page = 1,
    size = 20,
    activeFilters = filtersRef.current
  ) => {
    const requestScopeKey = auditScopeKey;
    const request = requestGateRef.current.start(requestScopeKey);
    setLoading(true);
    setReadError(null);
    try {
      const params = new URLSearchParams({
        limit: size.toString(),
        offset: ((page - 1) * size).toString(),
      });

      if (effectiveAuditScope === "all") {
        params.append("scope", "all");
      }
      if (warehouseId) params.append('warehouseId', String(warehouseId));
      if (activeFilters.includeAuth === false) params.append('includeAuth', 'false');
      if (activeFilters.criticalOnly) params.append('criticalOnly', 'true');

      if (activeFilters.userSearch?.trim()) {
        params.append("userSearch", activeFilters.userSearch.trim());
      }
      if (activeFilters.module) params.append("module", activeFilters.module);
      if (activeFilters.actionType) params.append("actionType", activeFilters.actionType);
      if (activeFilters.status) params.append("status", activeFilters.status);
      if (activeFilters.ipAddress) params.append("ipAddress", activeFilters.ipAddress);
      if (activeFilters.dateRange) {
        params.append("startDate", activeFilters.dateRange[0].format("YYYY-MM-DD HH:mm:ss.SSS"));
        params.append("endDate", activeFilters.dateRange[1].format("YYYY-MM-DD HH:mm:ss.SSS"));
      }
      const queryKey = `${auditScopeKey}:${params}`;
      if (publishedLogsQueryRef.current !== queryKey) {
        setLogs([]); setTotal(0); setHasLoaded(false); setGeneratedAt(null);
      }

      const response = await axios.get(`${API_CONFIG.BASE_URL}/api/audit/logs?${params}`, {
        headers: { Authorization: `Bearer ${token}`, ...(selectedCompany ? { 'X-Company-Id': String(selectedCompany) } : {}) },
        signal: request.signal,
      });

      if (!response.data?.success || !Array.isArray(response.data.logs)) throw new Error('INVALID_AUDIT_RESPONSE');
      if (request.isCurrent(latestAuditScopeKeyRef.current)) {
        setLogs(response.data.logs || []);
        setTotal(Number(response.data.total || 0));
        setGeneratedAt(new Date().toISOString());
        setHasLoaded(true);
        publishedLogsQueryRef.current = queryKey;
      }
    } catch (error) {
      if (
        isAuditRequestCanceled(error, request.signal) ||
        !request.isCurrent(latestAuditScopeKeyRef.current)
      ) {
        return;
      }
      setReadError('Impossible de charger les événements. Réessayez.');
    } finally {
      const isCurrent = request.isCurrent(latestAuditScopeKeyRef.current);
      request.finish();
      if (isCurrent) {
        setLoading(false);
      }
    }
  }, [auditScopeKey, effectiveAuditScope, selectedCompany, token, warehouseId]);
  const refreshLogs = () => loadLogs(currentPage, pageSize, filtersRef.current);

  useEffect(() => {
    const scopeChanged = previousAuditScopeKeyRef.current !== auditScopeKey;
    previousAuditScopeKeyRef.current = auditScopeKey;

    if (scopeChanged) {
      requestGateRef.current.cancel();
      setLogs([]);
      setTotal(0);
      setGeneratedAt(null);
      setReadError(null);
      setHasLoaded(false);
      publishedLogsQueryRef.current = null;
      setSelectedLog(null);
      setDetailVisible(false);

      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }

    loadLogs(currentPage, pageSize, filtersRef.current);
  }, [auditScopeKey, currentPage, pageSize, loadLogs]);

  useEffect(
    () => () => {
      requestGateRef.current?.cancel();
    },
    []
  );

  const applyFilters = () => {
    filtersRef.current = filters;
    setAppliedFilters(filters);
    const firstPage = 1;
    if (shouldLoadAuditPageImmediately(currentPage)) {
      loadLogs(firstPage, pageSize, filters);
      return;
    }

    setCurrentPage(firstPage);
  };

  const resetFilters = () => {
    const resetValues = {
      userSearch: "",
      module: null,
      actionType: null,
      status: null,
      dateRange: null,
      ipAddress: null,
      includeAuth: true,
      criticalOnly: false,
    };

    const firstPage = 1;
    setFilters(resetValues);
    filtersRef.current = resetValues;
    setAppliedFilters(resetValues);
    if (shouldLoadAuditPageImmediately(currentPage)) {
      loadLogs(firstPage, pageSize, resetValues);
      return;
    }

    setCurrentPage(firstPage);
  };

  const showLogDetails = (record) => {
    setSelectedLog(record);
    setDetailVisible(true);
  };

  // Un lien « Voir » du tableau de bord ouvre l'événement exact, même lorsqu'il
  // ne se trouve pas dans la première page du journal. La portée reste serveur.
  const detailRequestGateRef = useRef(null);
  if (!detailRequestGateRef.current) detailRequestGateRef.current = createAuditRequestGate();
  const requestedLogId = searchParams.get('logId');
  useEffect(() => {
    if (!requestedLogId || !/^\d+$/.test(requestedLogId)) return undefined;
    const request = detailRequestGateRef.current.start(auditScopeKey);
    axios.get(`${API_CONFIG.BASE_URL}/api/audit/logs/${requestedLogId}${effectiveAuditScope === 'all' ? '?scope=all' : ''}`, {
      headers: { Authorization: `Bearer ${token}`, ...(selectedCompany ? { 'X-Company-Id': String(selectedCompany) } : {}) }, signal: request.signal,
    }).then(({ data }) => {
      if (!data?.success || !data.data) throw new Error('INVALID_AUDIT_DETAIL_RESPONSE');
      if (request.isCurrent(latestAuditScopeKeyRef.current)) {
        setSelectedLog(data.data); setDetailVisible(true);
      }
    }).catch((error) => {
      if (!isAuditRequestCanceled(error, request.signal) && request.isCurrent(latestAuditScopeKeyRef.current)) {
        setReadError('Impossible de consulter cet événement dans le périmètre sélectionné.');
      }
    }).finally(() => request.finish());
    return () => detailRequestGateRef.current.cancel();
  }, [auditScopeKey, requestedLogId, token, selectedCompany, effectiveAuditScope]);
  const closeLogDetails = () => {
    detailRequestGateRef.current.cancel(); setDetailVisible(false); setSelectedLog(null);
    if (requestedLogId) setSearchParams((params) => { params.delete('logId'); return params; }, { replace: true });
  };

  const handlePaginationChange = (page, size) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  };

  const parsedSelectedLogDetails = parseAuditDetails(selectedLog?.details);
  const selectedLogDetailEntries = extractFlatDetails(parsedSelectedLogDetails);
  const selectedLogChangedFields = extractChangedFields(parsedSelectedLogDetails);
  const selectedLogBeforeAfterPairs = extractBeforeAfterPairs(parsedSelectedLogDetails);
  const selectedLogActionSummary = buildActionSummary(
    selectedLog,
    parsedSelectedLogDetails
  );

  const renderDetailValue = (value) => {
    if (value === undefined || value === null || value === "") {
      return <Text type="secondary">N/A</Text>;
    }

    if (typeof value === "boolean") {
      return <Text>{value ? "Oui" : "Non"}</Text>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <Text type="secondary">[]</Text>;
      }

      const primitiveOnly = value.every(
        (item) =>
          item === null ||
          ["string", "number", "boolean"].includes(typeof item)
      );

      if (primitiveOnly) {
        return (
          <Space wrap>
            {value.map((item, index) => (
              <Tag key={`${String(item)}-${index}`}>{String(item)}</Tag>
            ))}
          </Space>
        );
      }

      return (
        <pre style={{ fontSize: 12, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    if (isPlainObject(value)) {
      return (
        <pre style={{ fontSize: 12, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    return <Text>{String(value)}</Text>;
  };

  const columns = [
    { title: 'Date et heure (UTC)', dataIndex: 'created_at', key: 'date', width: 145, fixed: 'left',
      render: (value) => <Tooltip title={auditDateLabel(value, { seconds: true })}>{auditDateLabel(value, { short: true })}</Tooltip>,
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix() },
    { title: 'Utilisateur', key: 'user', width: 140, render: (_, row) => getUserDisplay(row) },
    { title: 'Entreprise', dataIndex: 'company_name', width: 160, render: (value, row) => value || (row.company_id ? `Entreprise #${row.company_id}` : '—') },
    { title: 'Établissement', dataIndex: 'warehouse_name', width: 135, render: (value, row) => value || (row.warehouse_id ? `Établissement #${row.warehouse_id}` : '—') },
    { title: 'Module', dataIndex: 'module', width: 145, render: getModuleLabel },
    { title: 'Action', dataIndex: 'action_type', width: 130, render: auditActionLabel },
    { title: 'Statut', dataIndex: 'status', width: 100, render: renderStatusTag },
    { title: 'Ressource', key: 'resource', width: 175, render: (_, row) => row.resource_type ? `${row.resource_type} #${row.resource_id || '—'}` : '—' },
    { title: 'Méthode', dataIndex: 'method', width: 85, render: renderMethodTag },
    { title: 'Détails', key: 'detail', width: 90, fixed: 'right', render: (_, row) =>
      <Button type="link" icon={<EyeOutlined />} aria-label={`Détails de l’événement ${row.id}`} onClick={() => showLogDetails(row)}>Détails</Button> },
  ];

  const renderMobileLogCard = (row) => <article className="audit-record-card" key={row.id}>
    <div className="audit-record-card-head"><div><strong>{auditActionLabel(row.action_type)}</strong><p>{getModuleLabel(row.module)}</p></div><AuditStatus status={row.status} /></div>
    <Text type="secondary">{auditDateLabel(row.created_at)} UTC</Text>
    <dl><dt>Utilisateur</dt><dd>{getUserDisplay(row)}</dd><dt>Entreprise</dt><dd>{row.company_name || '—'}</dd>
      <dt>Établissement</dt><dd>{row.warehouse_name || '—'}</dd><dt>Ressource</dt><dd>{row.resource_type ? `${row.resource_type} #${row.resource_id || '—'}` : '—'}</dd></dl>
    <Button icon={<EyeOutlined />} onClick={() => showLogDetails(row)}>Détails de l’événement</Button>
  </article>;

  const renderBeforeAfterDetails = () => {
    if (selectedLog?.action_type !== 'UPDATE' || !selectedLogBeforeAfterPairs.length) return null;
    return <Descriptions.Item label="Avant / Après"><div style={{ overflowX: 'auto' }}><table className="audit-comparison">
      <thead><tr><th>Champ</th><th>Avant</th><th>Après</th></tr></thead>
      <tbody>{selectedLogBeforeAfterPairs.map((pair) => <tr key={pair.field}><th scope="row">{formatDetailKey(pair.field)}</th>
        <td>{renderDetailValue(pair.before)}</td><td>{renderDetailValue(pair.after)}</td></tr>)}</tbody>
    </table></div></Descriptions.Item>;
  };

  const renderActionDataDetails = () => {
    if (!ACTIONS_WITH_ENRICHED_DETAILS.has(selectedLog?.action_type) || !selectedLogDetailEntries.length) return null;
    return <Descriptions.Item label="Données de l’action"><Descriptions size="small" column={1}>
      {selectedLogDetailEntries.map((entry) => <Descriptions.Item key={entry.key} label={formatDetailKey(entry.key)}>{renderDetailValue(entry.value)}</Descriptions.Item>)}
    </Descriptions></Descriptions.Item>;
  };
  const pagination = { current: currentPage, pageSize, total, showSizeChanger: true, pageSizeOptions: [20, 50, 100, 200],
    showTotal: (value, range) => `${range[0]}–${range[1]} sur ${value}`, onChange: handlePaginationChange };

  return <AuditWorkspace title="Journal d’audit" subtitle="Retrouver une action et comprendre les changements effectués."
    mobile={isMobile} updatedAt={generatedAt} actions={<Button icon={<ReloadOutlined />} loading={loading} onClick={refreshLogs}>Actualiser</Button>}>
    <AuditToolbar mobile={isMobile} summary={'Période, utilisateur, module et statut'}>
      <div className="audit-toolbar-row">
        <AuditField label="Utilisateur" htmlFor="audit-log-user" className="audit-field--wide"><Input id="audit-log-user" prefix={<SearchOutlined />}
          placeholder="Nom, email ou identifiant" value={filters.userSearch} onChange={(event) => setFilters({ ...filters, userSearch: event.target.value })} onPressEnter={applyFilters} allowClear /></AuditField>
        <AuditField label="Module" htmlFor="audit-log-module"><Select id="audit-log-module" aria-label="Module" allowClear placeholder="Tous les modules"
          value={filters.module} onChange={(value) => setFilters({ ...filters, module: value || null })} options={availableModules} /></AuditField>
        <AuditField label="Action" htmlFor="audit-log-action"><Select id="audit-log-action" aria-label="Action" allowClear placeholder="Toutes les actions"
          value={filters.actionType} onChange={(value) => setFilters({ ...filters, actionType: value || null })} options={actionTypes.map((value) => ({ value, label: auditActionLabel(value) }))} /></AuditField>
        <AuditField label="Statut" htmlFor="audit-log-status"><Select id="audit-log-status" aria-label="Statut" allowClear placeholder="Tous les statuts"
          value={filters.status} onChange={(value) => setFilters({ ...filters, status: value || null })}
          options={[{ value: 'success', label: 'Succès' }, { value: 'failure', label: 'Échec' }, { value: 'partial', label: 'Partiel' }]} /></AuditField>
        <div className="audit-filter-actions"><Button type="primary" data-audit-apply icon={<FilterOutlined />} onClick={applyFilters} loading={loading}>Filtrer</Button></div>
      </div>
      <div className="audit-toolbar-row">
        {isSuperAdmin ? <AuditField label="Périmètre" htmlFor="audit-log-scope" className="audit-field--scope"><Select id="audit-log-scope" aria-label="Périmètre"
          value={auditScope} onChange={(value) => { closeLogDetails(); setCurrentPage(1); setAuditScope(value); }}
          options={[{ value: 'company', label: 'Entreprise sélectionnée' }, { value: 'all', label: 'Toutes les entreprises' }]} /></AuditField> : null}
        <AuditField label="Période (UTC)" htmlFor="audit-log-dates" className="audit-field--dates"><RangePicker id="audit-log-dates" value={filters.dateRange}
          format="DD/MM/YYYY" placeholder={['Date de début', 'Date de fin']}
          onChange={(dates) => setFilters({ ...filters, dateRange: dates ? [dates[0].startOf('day'), dates[1].endOf('day')] : null })} /></AuditField>
        <Button type="text" onClick={() => setAdvancedOpen(!advancedOpen)} aria-expanded={advancedOpen}>Filtres avancés</Button>
        <Button type="link" className="audit-reset" onClick={resetFilters}>Réinitialiser</Button>
      </div>
      {advancedOpen ? <div className="audit-toolbar-row">
        <AuditField label="Adresse IP" htmlFor="audit-log-ip"><Input id="audit-log-ip" value={filters.ipAddress || ''} allowClear placeholder="Rechercher une adresse IP"
          onChange={(event) => setFilters({ ...filters, ipAddress: event.target.value })} onPressEnter={applyFilters} /></AuditField>
        <Checkbox checked={Boolean(filters.criticalOnly)} onChange={(event) => setFilters({ ...filters, criticalOnly: event.target.checked })}>Événements critiques uniquement</Checkbox>
        <div className="audit-switch-field"><Switch id="audit-log-auth" checked={filters.includeAuth !== false} onChange={(value) => setFilters({ ...filters, includeAuth: value })} />
          <label htmlFor="audit-log-auth">Inclure les connexions</label></div>
      </div> : null}
    </AuditToolbar>
    {appliedFilters.criticalOnly || appliedFilters.includeAuth === false ? <Space wrap style={{ marginBottom: 12 }}>
      {appliedFilters.criticalOnly ? <Tag>Événements critiques</Tag> : null}{appliedFilters.includeAuth === false ? <Tag>Connexions exclues</Tag> : null}
      <Button type="link" onClick={resetFilters}>Retirer les filtres du tableau de bord</Button>
    </Space> : null}
    <AuditReadError error={readError} hasData={hasLoaded} retry={refreshLogs} />
    <div className="audit-results-heading"><h2>{hasLoaded ? `${Number(total).toLocaleString('fr-FR')} événement${Number(total) > 1 ? 's' : ''} trouvé${Number(total) > 1 ? 's' : ''}` : 'Événements d’audit'}</h2>
      <Text type="secondary">{warehouseId && effectiveAuditScope !== 'all' ? 'Établissement sélectionné' : effectiveAuditScope === 'all' ? 'Toutes les entreprises' : 'Entreprise sélectionnée'}</Text></div>
    {isMobile ? <>
      <List loading={loading} dataSource={logs} split={false} renderItem={renderMobileLogCard}
        locale={{ emptyText: readError && !hasLoaded ? 'Données indisponibles' : 'Aucun événement trouvé' }} />
      {hasLoaded && total > 0 ? <Pagination {...pagination} size="small" className="audit-pagination" /> : null}
    </> : <div className="audit-table-surface"><Table columns={columns} dataSource={logs} loading={loading} rowKey="id" size="middle" pagination={pagination}
      scroll={{ x: 1260 }} locale={{ emptyText: readError && !hasLoaded ? 'Données indisponibles' : 'Aucun événement trouvé' }} /></div>}
    <Modal title="Détail de l’événement" open={detailVisible} onCancel={closeLogDetails} footer={<Button onClick={closeLogDetails}>Fermer</Button>}
      width={isMobile ? 'calc(100% - 24px)' : 900} centered destroyOnClose>
      {selectedLog ? <AuditDetailContent>
        {selectedLogActionSummary && ACTIONS_WITH_ENRICHED_DETAILS.has(selectedLog.action_type) ? <Alert showIcon style={{ marginBottom: 16 }}
          type={selectedLog.action_type === 'DELETE' ? 'warning' : selectedLog.action_type === 'UPDATE' ? 'info' : 'success'} message={selectedLogActionSummary} /> : null}
        <Descriptions column={isMobile ? 1 : 2} size="small" bordered>
          <Descriptions.Item label="Date et heure (UTC)">{auditDateLabel(selectedLog.created_at, { seconds: true })}</Descriptions.Item>
          <Descriptions.Item label="Utilisateur">{getUserDisplay(selectedLog)}</Descriptions.Item>
          <Descriptions.Item label="Entreprise">{selectedLog.company_name || (selectedLog.company_id ? `Entreprise #${selectedLog.company_id}` : '—')}</Descriptions.Item>
          <Descriptions.Item label="Établissement">{selectedLog.warehouse_name || (selectedLog.warehouse_id ? `Établissement #${selectedLog.warehouse_id}` : '—')}</Descriptions.Item>
          <Descriptions.Item label="Module">{getModuleLabel(selectedLog.module)}</Descriptions.Item>
          <Descriptions.Item label="Action">{auditActionLabel(selectedLog.action_type)}</Descriptions.Item>
          <Descriptions.Item label="Statut"><AuditStatus status={selectedLog.status} /></Descriptions.Item>
          <Descriptions.Item label="Ressource">{selectedLog.resource_type ? `${selectedLog.resource_type} #${selectedLog.resource_id || '—'}` : '—'}</Descriptions.Item>
          {selectedLog.action_type === 'UPDATE' && selectedLogChangedFields.length ? <Descriptions.Item label="Champs modifiés" span={isMobile ? 1 : 2}>
            <Space wrap>{selectedLogChangedFields.map((field) => <Tag key={field}>{formatDetailKey(field)}</Tag>)}</Space></Descriptions.Item> : null}
        </Descriptions>
        <Descriptions column={1} style={{ marginTop: 16 }}>{renderBeforeAfterDetails()}{renderActionDataDetails()}</Descriptions>
        <Collapse className="audit-advanced" items={[
          { key: 'technical', label: 'Informations techniques', children: <Descriptions column={1} size="small">
            <Descriptions.Item label="Identifiant de l’événement">{selectedLog.id}</Descriptions.Item><Descriptions.Item label="Méthode HTTP">{selectedLog.method || '—'}</Descriptions.Item>
            <Descriptions.Item label="Durée (ms)">{selectedLog.duration_ms ?? '—'}</Descriptions.Item><Descriptions.Item label="Adresse IP">{selectedLog.ip_address || '—'}</Descriptions.Item>
            <Descriptions.Item label="Navigateur">{selectedLog.user_agent || '—'}</Descriptions.Item></Descriptions> },
          { key: 'raw', label: 'Données brutes', children: parsedSelectedLogDetails ? <pre className="audit-technical-data">{JSON.stringify(parsedSelectedLogDetails, null, 2)}</pre> : 'Aucun détail' },
        ]} />
      </AuditDetailContent> : null}
    </Modal>
  </AuditWorkspace>;
};

export default AuditLogs;
